import { boot } from "quasar/wrappers";

// IMPORTANT — tree-shaking + bundle size:
// We import ONLY the named functions we actually call. Previously this file did
// `import * as Sentry from "@sentry/vue"` and then re-exported the whole namespace
// (`export { Sentry }`) AND assigned it to `app.config.globalProperties.$sentry`.
// Assigning/exporting the *entire* namespace object lets it "escape", which forces
// webpack to retain EVERY Sentry export — including Session Replay
// (`@sentry-internal/replay` + `replay-canvas`) and the Feedback widget
// (`@sentry-internal/feedback`) — even though they were never used. That dead
// weight (~190 KB parsed / ~65 KB gzip) was shipped eagerly on every cold load.
// Named imports + a small exported facade (below) let the bundler drop them.
//
// Safari 12 / iOS 12 note: @sentry is in `transpileDependencies` (quasar.config.js),
// so Babel down-levels its modern syntax. Keep this file Safari-12 safe too
// (no optional chaining / nullish coalescing).
import {
  init,
  browserTracingIntegration,
  captureException,
  captureMessage,
  addBreadcrumb,
  setUser,
  setTag,
  setContext,
} from "@sentry/vue";

/* eslint-env commonjs */
var qbankConfig = require('../config/qbank.js');
// vdom-corruption family detection + highlight-engine flag (Safari-12-safe .js)
var highlightContainment = require('../utils/highlightContainment.js');
// Lesson-side "highlights as data" renderer flag (Safari-12-safe .js)
var lessonHighlights = require('../utils/lessonHighlights.js');

// Read the ymp_user_id cookie set by Laravel's SetSentryUserCookie middleware.
// The QBank SPA is served as static HTML by nginx (no SSR), so this cookie is
// the only way to tag Sentry events with user identity BEFORE the /quiz-app/init
// XHR runs. Without it, any failure during init produces anonymous events.
// Safari 12 compatible — no optional chaining, no template literals here.
function readYmpUserCookie() {
  var match = document.cookie.match(/(?:^|;\s*)ymp_user_id=([^;]+)/);
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch (e) {
    return null;
  }
}

export default boot(({ app, router }) => {
  // Only initialize Sentry in production
  if (process.env.NODE_ENV === 'production') {
    init({
      app,
      dsn: "https://24dcaea945c97a7e569b13d204269da3@o4510427388444672.ingest.us.sentry.io/4510427403517952",

      // Setting this option to true will send default PII data to Sentry
      // For example, automatic IP address collection on events
      sendDefaultPii: true,

      // Set the environment
      environment: process.env.NODE_ENV,

      // Optionally set a release version
      // release: "quasarquiz@1.0.0",

      // Integration with Vue Router for better route tracking.
      // Session Replay (replayIntegration) intentionally REMOVED — it was the
      // single heaviest sub-piece of Sentry (~188 KB parsed / ~60 KB gzip,
      // rrweb-based) and is rarely worth that on every cold load. Error capture
      // and performance tracing are unaffected.
      integrations: [
        browserTracingIntegration({ router }),
      ],

      // Tracing - Capture 100% of transactions for initial testing
      // Lower to 0.1-0.2 in production to reduce costs
      tracesSampleRate: 0.1,

      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/yourmedpass\.com/],

      // Enable Logs
      enableLogs: true,

      // Filter out known non-critical errors
      // Note: Avoiding optional chaining (?.) for Safari 12/iOS 12 compatibility
      beforeSend(event) {
        // Ignore chunk loading errors (already handled by router)
        var exceptionValues = event && event.exception && event.exception.values;
        var firstValue = exceptionValues && exceptionValues[0];
        var errorValue = firstValue && firstValue.value;
        // ...except the router's own report that a chunk STILL fails after
        // its one automatic reload (router/index.js) — that one is real.
        var stillFailingChunk = event && event.tags && event.tags.errorType === 'chunk_reload_failed';
        if (errorValue && errorValue.includes('Loading chunk') && !stillFailingChunk) {
          return null;
        }

        // Drop client-side AxiosError "Network Error" events. These are
        // unactionable connectivity drops (a student's wifi/4G dropping mid-quiz),
        // not server bugs — they were the dominant contributor to the catch-all
        // axios error group and masked real server-side errors in the dashboard.
        var errorType = firstValue && firstValue.type;
        if (errorType === 'AxiosError' && errorValue && errorValue.indexOf('Network Error') !== -1) {
          return null;
        }

        // Drop AxiosError "Request aborted": the XHR's onabort fired, which
        // only happens when the BROWSER tears the request down (page reload,
        // navigation, the desktop app closing a window). Our own cancels are
        // ERR_CANCELED and never reach here; timeouts say "timeout" and stay
        // captured (Sentry QBANK-FRONTEND-1ZJ / 176).
        if (errorType === 'AxiosError' && errorValue === 'Request aborted') {
          return null;
        }

        // Drop "Object Not Found Matching Id:N, MethodName:update,
        // ParamCount:4" — a rejection raised by Microsoft's Outlook/Defender
        // Safe Links scanner driving the page through its own bridge, not by
        // our code (Sentry QBANK-FRONTEND-1ZK: no stack, datacenter IP, no
        // user). Matched on the full fingerprint so nothing else is hidden.
        if (
          errorValue &&
          errorValue.indexOf('Object Not Found Matching Id:') !== -1 &&
          errorValue.indexOf('MethodName:') !== -1
        ) {
          return null;
        }

        // Drop non-Error promise rejections that reject with a DOM Event —
        // "Event `Event` (type=error) captured as promise rejection". These
        // come from websocket/RTC internals (LiveKit/Pusher) rejecting with
        // the raw error EVENT on connection drops; the payload carries zero
        // stack/context and is pure connectivity noise (Sentry
        // QBANK-FRONTEND-ZP: 500+ events, all correlating with ping
        // timeouts). Real app failures reject with Error objects and are
        // unaffected.
        if (
          errorValue &&
          errorValue.indexOf('captured as promise rejection') !== -1 &&
          /^Event `\w+` \(type=\w+\)/.test(errorValue)
        ) {
          return null;
        }

        // Group the vdom-corruption family under ONE fingerprint. These are
        // all the same root cause (external DOM surgery on Vue-managed nodes
        // -> half-mounted tree -> cascading patch/unmount errors), but the
        // obfuscator renames variables inside the error MESSAGES every build,
        // so Sentry was splintering the family into a new issue per deploy.
        // Gated on the Vue error-handler mechanism so an unrelated app error
        // with a similar message cannot be mis-grouped.
        var mechanism = firstValue && firstValue.mechanism;
        var mechanismType = mechanism && mechanism.type;
        if (
          errorValue &&
          mechanismType &&
          mechanismType.indexOf('vue') !== -1 &&
          highlightContainment.isVdomCorruptionMessage(errorValue)
        ) {
          event.fingerprint = ['ymp-vue-tree-corruption'];
          if (!event.tags) event.tags = {};
          event.tags.corruption_family = 'vue-tree-corruption';
          // 'strict' = property-name-pinned match (certain corruption);
          // 'broad' = generic/obfuscated shape (could be an unrelated bug
          // folded into the group) — reviewable filter in Sentry.
          event.tags.corruption_pattern = highlightContainment.corruptionPatternTier(errorValue);
        }

        return event;
      },

      // Add additional context
      initialScope: {
        tags: {
          qbank_source: qbankConfig.getConfig().qbank_source || 'unknown',
          // Which highlight engine this session runs — lets us compare the
          // corruption-family event rate per cohort and prove the fix.
          highlight_engine: highlightContainment.isEnabled() ? 'contained_v1' : 'legacy',
          // Phase C: which QUESTION annotation engine this session runs (baked
          // spans vs legacy live-DOM surgery). Cohorts the rollout soak.
          question_annotation_engine: highlightContainment.isAnnotationRendererEnabled() ? 'anchors_v1' : 'legacy',
          // Which LESSON highlight engine this session runs (library viewer).
          // Cohorts the "highlights as data" rollout during the manual soak.
          lesson_highlight_engine: lessonHighlights.isEnabled() ? 'anchors_v1' : 'legacy',
          // Which LESSON annotation engine this session runs (Phase B).
          lesson_annotation_engine: lessonHighlights.isAnnotationEnabled() ? 'anchors_v1' : 'legacy',
        },
      },
    });

    // Tag this Sentry session with user identity from cookie, if available.
    // MainLayout.vue will overwrite this with fresh data once /init succeeds —
    // this just covers the window before /init returns (or when /init fails).
    var cookieUser = readYmpUserCookie();
    if (cookieUser && cookieUser.id) {
      setUser({
        id: String(cookieUser.id),
        username: cookieUser.username,
        email: cookieUser.email,
        ip_address: '{{auto}}',
      });
    }
  }

  // vdom-corruption detector. When a Vue render/patch/unmount error matches
  // the corruption family (external DOM surgery corrupted the component tree
  // — see src/utils/highlightContainment.js), notify the current page so it
  // can self-heal (remount the question region, or reload as last resort).
  // Wraps whatever errorHandler is already installed (Sentry's, in
  // production) and ALWAYS calls through — it never swallows an error.
  var prevErrorHandler = app.config.errorHandler;
  app.config.errorHandler = function (err, instance, info) {
    try {
      var msg = err && err.message ? err.message : String(err);
      // STRICT matcher only: the broad/obfuscated patterns are for Sentry
      // grouping — they may match unrelated bugs and must never be able to
      // remount/reload a student's session. Separate kill switch:
      // localStorage 'qbank_vdom_breaker' = '0'.
      if (
        highlightContainment.isVdomCorruptionMessageStrict(msg) &&
        highlightContainment.isBreakerEnabled()
      ) {
        // `component` = the Vue component whose patch failed (e.g.
        // 'RouterView' when a whole page swap broke) — lets a listener act
        // only on the failure it can actually repair.
        var internal = instance && instance.$ ? instance.$ : null;
        var componentName = internal && internal.type && internal.type.name ? internal.type.name : '';
        window.dispatchEvent(
          new CustomEvent('ymp:vdom-corruption', { detail: { message: msg, component: componentName } })
        );
      }
    } catch (e) {
      // the detector must never interfere with error handling itself
    }
    if (typeof prevErrorHandler === 'function') {
      prevErrorHandler(err, instance, info);
    } else {
      // development / uninitialized-Sentry fallback: keep errors visible
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  // Expose the lightweight facade globally for manual error capturing.
  // In production these call into the initialized SDK; in development they are
  // real-but-uninitialized no-ops (no errors are sent without init()).
  app.config.globalProperties.$sentry = Sentry;
});

// Lightweight facade exported for use in components and non-component files.
// Exposes ONLY the methods the app actually uses — deliberately NOT the full
// `@sentry/vue` namespace (see the top-of-file note on tree-shaking). None of
// these named exports pull in Session Replay or the Feedback widget.
export const Sentry = {
  captureException,
  captureMessage,
  addBreadcrumb,
  setUser,
  setTag,
  setContext,
};
